/* eslint-disable import/no-extraneous-dependencies */
import { Route, Routes, Outlet, Navigate, useLocation } from 'react-router-dom';

import { getModulePath, LEAGUE_MODULE_NAMES } from '@leagueplatform/core';
import { RewardsEventController } from '@leagueplatform/rewards-events';
import { AchievementsRoutes } from '@leagueplatform/rewards-achievements';
import {
  AuthViewLogin,
  AuthViewLogout,
  AuthStartImpersonationSession,
} from 'pages/auth/Auth.pages';

import {
  LandingPage,
  CareCollectionsRoutes,
  // CareDiscoveryRoutes,
  HealthJourneyRoutes,
  HealthNeedPages,
  HealthProfileRoutes,
  AboutMeRoutes,
  Home,
  OnboardingRoutesWithProvider,
  WalletRoutes,
  ClaimsRoutes,
} from 'components/LazyRoutes';

import ErrorPage from 'pages/error-404';
import { PATHS } from 'commons/constants';
import { AboutUsPage } from 'pages/about-us/about-us.page';
import { FormsPage } from 'pages/forms/Forms.page';
import { ContactUsPage } from 'pages/contact-us/contact-us.page';
import { PersonalInformationPage } from 'pages/personal-information.page';
import { CommunicationPreferencesPage } from 'pages/communication-preferences/communication-preferences.page';
import { ChangePCPPage } from 'pages/change-pcp/change-pcp.page';
import { AuthorizationPage } from 'pages/authorizations/authorizations.page';
import { TempCareLandingPage } from 'pages/temporary-get-care-landing.page';
import { AuthorizationDetailPage } from 'pages/authorizations/authorizations-details.page';
import { ProviderRedirectPage } from 'pages/provider-redirect.page';
import useSyncRouteWithBrowser from 'hooks/use-sync-route-with-browser.hook';
import useConditionalNavigation from 'hooks/use-conditional-navigation.hook';
import { useInterceptLinkClick } from 'hooks/use-intercept-link-click.hook';
import { useCoverageStatus } from 'hooks/use-coverage-status.hook';
import { PrivateRoutesContainer } from './private-routes-container/private-routes-container.component';
import { AuthSessionContainer } from './auth-session-container/auth-session-container';

const AuthRoutesElement = () => (
  <AuthSessionContainer>
    <PrivateRoutesContainer>
      <Outlet />
      <RewardsEventController />
    </PrivateRoutesContainer>
  </AuthSessionContainer>
);

/** League SDK links sometimes use a `/member` prefix; we only register routes without it. */
function StripMemberPrefixNavigate() {
  const { pathname, search, hash } = useLocation();
  const nextPath = pathname.replace('/member', '') || '/';
  return <Navigate to={`${nextPath}${search}${hash}`} replace />;
}

/** Termed members lose access to forms submission; deep-links to /forms must redirect home. */
function FormsRoute() {
  const hasActiveCoverage = useCoverageStatus();
  if (!hasActiveCoverage) {
    return <Navigate to="/home" replace />;
  }
  return <FormsPage />;
}

function AppRoutes() {
  useSyncRouteWithBrowser();
  useConditionalNavigation();
  useInterceptLinkClick('/sso/vendors/', import.meta.env.VITE_LINKOUT_ROOT_URL);

  return (
    <Routes>
      <Route path="/sign-in/*" element={<AuthViewLogin />} />
      <Route path="/sign-out/*" element={<AuthViewLogout />} />
      <Route
        path="/start-login/*"
        element={<AuthStartImpersonationSession />}
      />
      <Route path="/" element={<LandingPage />} />
      <Route element={<AuthRoutesElement />}>
        <Route
          path="/member/*"
          element={<StripMemberPrefixNavigate />}
          key="strip-member-prefix"
        />
        <Route path="/home" element={<Home />} key="home" />
        <Route
          path={`${getModulePath(LEAGUE_MODULE_NAMES.healthJourney)}/*`}
          element={<HealthJourneyRoutes />}
          key="health-journey"
        />
        <Route
          path={`${getModulePath(LEAGUE_MODULE_NAMES.healthPrograms)}/*`}
          element={<HealthJourneyRoutes />}
          key="health-journey"
        />
        <Route
          path={`${getModulePath(LEAGUE_MODULE_NAMES.healthProgress)}/*`}
          element={<HealthJourneyRoutes />}
          key="health-journey"
        />
        <Route
          path={`${getModulePath(LEAGUE_MODULE_NAMES.healthJourneyHistory)}/*`}
          element={<HealthJourneyRoutes />}
          key="health-journey"
        />
        <Route
          path={`${getModulePath(LEAGUE_MODULE_NAMES.careCollections)}/*`}
          element={<CareCollectionsRoutes />}
          key="care-collections"
        />
        <Route
          path={`${getModulePath(LEAGUE_MODULE_NAMES.claims)}/*`}
          element={<ClaimsRoutes />}
          key="claims"
        />
        {/* <CompatRoute
              path={getModulePath(LEAGUE_MODULE_NAMES.careDiscovery)}
              component={CareDiscoveryRoutes}
              key="care-discovery"
            /> */}
        {/* to be replaced after SCFHP-874-update-get-care-masonry has been merged */}
        <Route
          path={`${getModulePath(LEAGUE_MODULE_NAMES.careDiscovery)}/*`}
          element={<TempCareLandingPage />}
          key="care-discovery"
        />
        <Route
          path={`${getModulePath(LEAGUE_MODULE_NAMES.healthNeedPages)}/*`}
          element={<HealthNeedPages />}
          key="health-needs"
        />
        <Route
          path={`${getModulePath(LEAGUE_MODULE_NAMES.wallet)}/*`}
          element={<WalletRoutes />}
          key="wallet"
        />
        <Route
          path={`${getModulePath(LEAGUE_MODULE_NAMES.healthProfile)}/*`}
          element={<HealthProfileRoutes />}
          key="health-profile"
        />
        <Route
          path={`${getModulePath(LEAGUE_MODULE_NAMES.aboutMe)}/*`}
          element={<AboutMeRoutes />}
          key="about-me"
        />
        <Route path={`${PATHS.FORMS}/*`} element={<FormsRoute />} key="forms" />
        <Route
          path={PATHS.CONTACT_US}
          element={<ContactUsPage />}
          key="contact-us"
        />
        <Route
          path={`${PATHS.PERSONAL_INFO}/*`}
          element={<PersonalInformationPage />}
        />
        <Route
          path={PATHS.COMMUNICATION_PREFERENCES}
          element={<CommunicationPreferencesPage />}
        />
        <Route path={PATHS.CHANGE_PCP} element={<ChangePCPPage />} />
        <Route
          path={PATHS.AUTH_STATUSES_PAGE}
          element={<AuthorizationPage />}
        />
        <Route
          path={getModulePath(LEAGUE_MODULE_NAMES.rewardsAchievements)}
          element={<AchievementsRoutes />}
        />

        <Route
          path={PATHS.AUTH_STATUS_PAGE}
          element={<AuthorizationDetailPage />}
        />
        {/* Submitted Requests is not released on web yet; send deep-links to the forms hub. */}
        <Route
          path={PATHS.FORM_STATUSES_PAGE}
          element={<Navigate to={PATHS.FORMS} replace />}
        />
        <Route
          path={PATHS.FORM_STATUS_PAGE}
          element={<Navigate to={PATHS.FORMS} replace />}
        />
        <Route path="/provider-redirect" element={<ProviderRedirectPage />} />

        <Route path={PATHS.ABOUT} element={<AboutUsPage />} />
        <Route
          path={`${getModulePath(LEAGUE_MODULE_NAMES.onboarding)}/*`}
          element={<OnboardingRoutesWithProvider contentfulId="" tenantId="" />}
          key="onboarding"
        />

        <Route element={<ErrorPage />} path="/*" />
      </Route>
    </Routes>
  );
}

export default AppRoutes;
